import { ClientType, PeerIdType, PeerMessage, PeerMessageType, PresenceMessageType, websocket } from '@playcastdotio/Messaging';
import * as Sentry from '@sentry/react';
import { getStateStore } from '../../../stores';
import { sendMessage, storePeerData } from '..';
import { hostHandlePeerMessage } from '../../peers/host';
import { USE_SESSION_COORDINATOR, USE_MEDIA_COORDINATOR } from '../../../../config/featureFlags';
import { realtimeSignalingService, gameInstancePeerManager } from '../../../../coordinators/init';

export const handlePeerMessage = (message: PeerMessage) => {
  Sentry.logger.info(`[PeerHandler] Peer message: ${message.type}`);
  switch (message.type) {
    case PeerMessageType.PEER_GET_ID_ANSWER: {
      Sentry.logger.info(
        `[PeerHandler] PEER_GET_ID_ANSWER peerId=${message.peerId} type=${message.peerIdType} hasToken=${!!message.peerToken} instanceIndex=${message.peerMetadata?.gameInstanceIndex ?? 'none'}`,
      );

      const target = message.peerIdType;

      // Game-instance HOST peers (matchmaking v2): these are extra HOST peer ids,
      // one per ready game instance, distinguished by peerMetadata.gameInstanceIndex.
      // Route them to the GameInstancePeerManager ONLY — they must not touch the
      // single-HOST bookkeeping below (token storage, hostPresence.hostPeerId,
      // presence update), and must not go through realtimeSignalingService (its
      // peerIdAssigned handlers would clobber the main host peer id).
      if (target === PeerIdType.HOST && message.peerMetadata?.gameInstanceIndex !== undefined) {
        gameInstancePeerManager.handlePeerIdAssigned(
          message.peerId,
          message.peerMetadata.gameInstanceIndex,
        );
        break;
      }

      const state = getStateStore('ipc');
      const hostPresence = state.hostPresenceState.currentState;

      if (message.peerToken) {
        Sentry.logger.info(`[PeerHandler] Storing token for peer: ${message.peerId}`);
        storePeerData(message.peerIdType, message.peerId, message.peerToken);
      } else {
        Sentry.logger.warn(`[PeerHandler] No token received for peer ID: ${message.peerId}`);
      }

      if (message.iceServers) {
        Sentry.logger.info(`[PeerHandler] Storing ICE servers for peer: ${message.peerId}`);
        state.iceServerState.updateState({
          ...state.iceServerState.currentState,
          hostIceServers: message.iceServers,
        });
      }

      // STRANGLER-FIG: Route to PeerCoordinator via signaling service
      if (USE_MEDIA_COORDINATOR) {
        realtimeSignalingService.handleIncomingMessage(message);
      }

      switch (target) {
        case PeerIdType.HOST: {
          Sentry.logger.info(`[PeerHandler] Host peer id: ${message.peerId}`);
          hostPresence.hostPeerId = message.peerId;
          break;
        }
        case PeerIdType.REMOTE_HOST: {
          Sentry.logger.info(`[PeerHandler] Remote host peer id: ${message.peerId}`);
          hostPresence.remoteHostPeerId = message.peerId;
          break;
        }
        case PeerIdType.PLAYER:
        case PeerIdType.REMOTE_CLIENT: {
          Sentry.logger.warn(`[PeerHandler] Got peer id for player or remote client: ${message.peerId}`);
          break;
        }
        default: {
          Sentry.logger.warn(`[PeerHandler] Unknown peer id target: ${target}`);
          break;
        }
      }
      Sentry.logger.info(
        `[PeerHandler] Updated host presence: hostPeerId=${hostPresence.hostPeerId ?? ''} remoteHostPeerId=${hostPresence.remoteHostPeerId ?? ''}`,
      );
      state.hostPresenceState.updateState({ ...hostPresence });

      // STRANGLER-FIG: Skip PRESENCE_UPDATE when SessionCoordinator is active
      if (!USE_SESSION_COORDINATOR) {
        sendMessage(
          websocket.generators.presence(PresenceMessageType.PRESENCE_UPDATE, {
            clientType: ClientType.HOST,
            presence: hostPresence,
          }),
        );
      }

      break;
    }
    case PeerMessageType.PEER_SDP_ANSWER:
    case PeerMessageType.PEER_SDP_OFFER:
    case PeerMessageType.PEER_ICE_CANDIDATE: {
      Sentry.logger.info(
        `[PeerHandler] Signalling: ${message.type} from=${message.fromId} to=${message.toId} useMediaCoord=${USE_MEDIA_COORDINATOR}`,
      );
      if (USE_MEDIA_COORDINATOR) {
        realtimeSignalingService.handleIncomingMessage(message);
      } else {
        hostHandlePeerMessage(message);
      }
      break;
    }
    case PeerMessageType.PEER_ERROR: {
      Sentry.logger.error(
        `[PeerHandler] Peer error: peerId=${message.peerId} code=${message.error.code} msg=${message.error.message}`,
      );
      break;
    }
    case PeerMessageType.PEER_GET_ID:
    case PeerMessageType.PEER_ICE_COMPLETE:
    case PeerMessageType.PEER_STATS:
    case PeerMessageType.PEER_STATS_SUCCESS: {
      Sentry.logger.info(`[PeerHandler] Unwanted peer message: ${message.type}`);
      break;
    }
  }
};
